<script lang="ts">
  import { onMount } from 'svelte';
  import {
    createRule,
    getTransactions,
    upsertTransaction,
    dismissRecurringPattern,
    getDismissedPatterns,
    undismissRecurringPattern,
    snoozeRecurringPattern,
    getSnoozedPatterns,
    updateRecurringInterval as saveRecurringInterval,
    getRecurringMetadata
  } from '../../lib/storage';
  import { detectRecurringTransactions, normalizeDescription, type RecurringPattern as DetectedPattern } from '../../lib/analytics/recurring/detector';
  import { extractMerchant } from '../../lib/analytics/recurring/normalizer';
  import { formatDate, formatAmount } from '../../lib/formatters';
  import { Button, StatCard } from '../common';
  import Notification from '../common/Notification.svelte';
  import RecurringPatternCard from './RecurringPatternCard.svelte';
  import RecurringFilters from './RecurringFilters.svelte';
  import RecurringItemsTable from './RecurringItemsTable.svelte';

  // Recurring utilities
  import { getPatternKey, isPatternDismissed, isPatternSnoozed } from '../../lib/recurring/patternUtils';
  import {
    groupRecurringTransactions,
    calculateRecurringSummary,
    getStatusBadgeClass,
    getStatusLabel
  } from '../../lib/recurring/transactionGrouping';
  import {
    archiveRecurringPattern,
    snoozeRecurringPattern as snoozePattern,
    updateRecurringInterval as saveInterval
  } from '../../lib/recurring/recurringActions';

  // Types
  import type { RecurringTransaction, RecurringSummary } from '../../types/recurring';

  // Sub-components
  import PossiblyEndedSection from './PossiblyEndedSection.svelte';
  import UpcomingPaymentsSection from './UpcomingPaymentsSection.svelte';

  // Event handlers for recurring actions
  async function handleArchive(pattern: string) {
    archivingPattern = pattern;
    try {
      const { patternKey } = await archiveRecurringPattern(pattern);
      dismissed = new Set(dismissed).add(patternKey);
      await loadRecurring();
    } catch (err: any) {
      error = err.message;
    }
    archivingPattern = null;
  }

  async function handleSnooze(pattern: string, days: number = 90) {
    snoozingPattern = pattern;
    try {
      await snoozePattern(pattern, days);
      snoozed = await loadSnoozedPatterns();
    } catch (err: any) {
      error = err.message;
    }
    snoozingPattern = null;
  }

  async function handleUpdateInterval(pattern: string, avgInterval: number) {
    try {
      await saveInterval(pattern, avgInterval);
      await loadRecurring();
    } catch (err: any) {
      error = err.message;
    }
  }

  // Use the type from detector which supports 'subscription' | 'bill'
  type RecurringPattern = DetectedPattern;

  let recurring = $state<RecurringTransaction[]>([]);
  let summary = $state<RecurringSummary>({
    totalMonthly: 0,
    subscriptions: { count: 0, total: 0 },
    bills: { count: 0, total: 0 }
  });
  let loading = $state<boolean>(false);
  let error = $state<string | null>(null);
  let activeTab = $state<'all' | 'subscriptions' | 'bills' | 'dismissed'>('all');
  let archivingPattern = $state<string | null>(null);
  let snoozingPattern = $state<string | null>(null);
  let editingIntervalPattern = $state<string | null>(null);
  let editIntervalValue = $state<number>(30);
  let showConfidenceTooltip = $state<Record<string, boolean>>({});

  // Pattern detection state
  let patterns = $state<RecurringPattern[]>([]);
  let scanning = $state<boolean>(false);
  let dismissed = $state<Set<string>>(new Set());
  let snoozed = $state<Set<string>>(new Set());
  let creatingRule = $state<string | null>(null);
  let successMessage = $state<string | null>(null);
  let patternsExpanded = $state<boolean>(false);
  let undismissingPattern = $state<string | null>(null);

  // Load dismissed patterns from storage
  async function loadDismissedPatterns(): Promise<Set<string>> {
    try {
      const dismissedList = await getDismissedPatterns();
      // Keep original patterns from database (don't normalize)
      // isPatternDismissed() handles checking both exact and normalized keys

      console.log('[RecurringDashboard] Loaded dismissed patterns:', {
        patterns: dismissedList
      });
      return new Set(dismissedList);
    } catch (err) {
      console.error('Error loading dismissed patterns:', err);
      return new Set();
    }
  }

  // Load snoozed patterns from storage
  async function loadSnoozedPatterns(): Promise<Set<string>> {
    try {
      const snoozedList = await getSnoozedPatterns();
      // Keep original patterns from database (don't normalize)
      // isPatternSnoozed() handles checking both exact and normalized keys
      const patterns = snoozedList.map((s: { pattern: string; snoozedUntil: string }) => s.pattern);

      console.log('[RecurringDashboard] Loaded snoozed patterns:', {
        patterns: patterns
      });
      return new Set(patterns);
    } catch (err) {
      console.error('Error loading snoozed patterns:', err);
      return new Set();
    }
  }

  onMount(async () => {
    // Load dismissed and snoozed patterns from storage
    dismissed = await loadDismissedPatterns();
    snoozed = await loadSnoozedPatterns();

    await loadRecurring();
    // Auto-scan for new patterns in the background
    detectRecurringPatterns();
  });

  async function loadRecurring() {
    loading = true;
    error = null;

    try {
      // Load recurring transactions from storage provider
      const allTransactions = await getTransactions();

      // Use utility to group and process recurring transactions
      recurring = groupRecurringTransactions(allTransactions);

      // Calculate summary statistics
      summary = calculateRecurringSummary(recurring);
    } catch (err: any) {
      error = err.message;
      console.error('Error loading recurring:', err);
    }

    loading = false;
  }

  let upcomingRecurring = $derived(
    recurring
      .filter(r => r.status === 'due_soon' || r.status === 'due_today')
      .sort((a, b) => a.daysUntilDue - b.daysUntilDue)
  );

  let filteredRecurring = $derived(
    activeTab === 'all'
      ? recurring
      : activeTab === 'subscriptions'
      ? recurring.filter(r => r.recurringType === 'subscription')
      : recurring.filter(r => r.recurringType === 'bill')
  );

  function startEditingInterval(pattern: string, currentInterval: number) {
    editingIntervalPattern = pattern;
    editIntervalValue = currentInterval;
  }

  function cancelEditingInterval() {
    editingIntervalPattern = null;
  }

  async function saveIntervalEdit(pattern: string) {
    try {
      await handleUpdateInterval(pattern, editIntervalValue);
      editingIntervalPattern = null;
    } catch (err: any) {
      error = err.message;
      editingIntervalPattern = null;
    }
  }

  let possiblyEndedItems = $derived(
    recurring.filter(r => {
      if (r.status !== 'possibly_ended') return false;

      // Check if pattern is snoozed using utility function
      return !isPatternSnoozed(r.displayName, snoozed);
    })
  );

  // Pattern detection functions
  async function detectRecurringPatterns(): Promise<void> {
    scanning = true;
    error = null;

    try {
      // Use the full recurring detection algorithm
      const allTransactions = await getTransactions();
      const detectedPatterns = detectRecurringTransactions(allTransactions, {
        minOccurrences: 3,  // At least 3 occurrences to suggest
        intervalTolerance: 5,  // ±5 days tolerance
        amountTolerance: 0.2   // 20% amount variance allowed
      });

      // Filter out stale patterns (monthly patterns with no occurrence in last 60 days)
      const now = new Date();
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

      // Get the most recent transaction date overall
      const mostRecentTransactionDate = allTransactions.length > 0
        ? new Date(Math.max(...allTransactions.map(t => new Date(t.date).getTime())))
        : now;

      patterns = detectedPatterns.filter(p => {
        const lastOccurrence = new Date(p.lastDate);
        const daysSinceLastOccurrence = Math.floor((now.getTime() - lastOccurrence.getTime()) / (24 * 60 * 60 * 1000));

        // If the pattern is roughly monthly (25-35 days) and hasn't occurred in 60 days
        // AND there are other recent transactions, it's likely cancelled
        if (p.avgInterval >= 25 && p.avgInterval <= 35 && daysSinceLastOccurrence > 60) {
          // Check if there are other transactions more recent than this pattern
          const hasRecentTransactions = mostRecentTransactionDate > lastOccurrence;
          if (hasRecentTransactions) {
            console.log(`[RecurringDashboard] Filtering out stale pattern: ${p.displayName} (last: ${p.lastDate}, ${daysSinceLastOccurrence} days ago)`);
            return false;
          }
        }

        return true;
      });

      console.log(`[RecurringDashboard] Detected ${patterns.length} recurring patterns (after filtering stale)`);
      if (patterns.length > 0) {
        console.log('[RecurringDashboard] Pattern details:', patterns.map(p => {
          const patternKey = getPatternKey(p.displayName);
          return {
            pattern: p.pattern,
            displayName: p.displayName,
            patternKey: patternKey,
            isDismissed: isPatternDismissed(p.displayName, dismissed)
          };
        }));
      }
      // Silently load patterns - no success message for auto-scan
    } catch (err: any) {
      // Silently fail - don't show errors for background scans
      console.error('Error detecting recurring:', err);
      patterns = [];
    }

    scanning = false;
  }

  async function dismissPattern(pattern: RecurringPattern): Promise<void> {
    try {
      // Store the pattern key (normalized) for consistent matching
      const patternKey = getPatternKey(pattern.displayName);
      console.log(`[RecurringDashboard] Dismissing detected pattern: "${pattern.pattern}" (key: "${patternKey}")`);
      await dismissRecurringPattern(patternKey);
      // Update local state - add the pattern key
      dismissed = new Set(dismissed).add(patternKey);
      // Also remove from patterns list immediately
      patterns = patterns.filter(p => p.pattern !== pattern.pattern);
      successMessage = `"${pattern.displayName}" marked as not recurring`;
      setTimeout(() => successMessage = null, 3000);
    } catch (err: any) {
      error = err.message || 'Failed to dismiss pattern';
      console.error('Error dismissing pattern:', err);
    }
  }

  async function markPatternAsCancelled(pattern: RecurringPattern, selectedType: 'subscription' | 'bill'): Promise<void> {
    creatingRule = pattern.pattern;
    error = null;

    try {
      // Create a rule for the pattern
      const cleanPattern = pattern.displayName
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

      await createRule({
        pattern: cleanPattern,
        category: pattern.category,
        person: null,
        normalizedName: pattern.displayName,
        markAsRecurring: true,
        recurringType: selectedType,
        excludeFromAnalytics: false
      });

      // Mark transactions as cancelled (isRecurring=true but with a cancelled flag)
      if (pattern.transactions && pattern.transactions.length > 0) {
        const allTransactions = await getTransactions();
        const transactionIdsSet = new Set(pattern.transactions);

        for (const transaction of allTransactions) {
          if (transactionIdsSet.has(transaction.id)) {
            await upsertTransaction({
              ...transaction,
              isRecurring: true,
              recurringType: selectedType,
              displayName: pattern.displayName,
              categorySource: 'rule',
              category: pattern.category,
              // TODO: Add a 'cancelled' or 'ended' field to mark this as no longer active
            });
          }
        }

        console.log(`[RecurringDashboard] Marked ${pattern.transactions.length} transactions as cancelled ${selectedType}`);
      }

      // Remove from patterns list
      patterns = patterns.filter(p => p.pattern !== pattern.pattern);

      const typeLabel = selectedType === 'subscription' ? 'subscription' : 'bill';
      successMessage = `"${pattern.displayName}" marked as cancelled ${typeLabel}`;
      setTimeout(() => successMessage = null, 3000);

      // Reload recurring transactions
      await loadRecurring();
    } catch (err: any) {
      error = err.message;
      console.error('Error marking as cancelled:', err);
    }

    creatingRule = null;
  }

  async function undismissPattern(pattern: string): Promise<void> {
    undismissingPattern = pattern;
    try {
      // Call API to remove dismissal
      await undismissRecurringPattern(pattern);
      // Update local state - create a new Set without the pattern
      const newDismissed = new Set(dismissed);
      newDismissed.delete(pattern);
      dismissed = newDismissed;

      successMessage = 'Pattern restored - it will appear in the next scan';
      setTimeout(() => {
        successMessage = null;
      }, 3000);
    } catch (err: any) {
      error = err.message || 'Failed to restore pattern';
      console.error('Error un-dismissing pattern:', err);
    }
    undismissingPattern = null;
  }

  async function createRuleFromPattern(pattern: RecurringPattern, selectedType: 'subscription' | 'bill'): Promise<void> {
    creatingRule = pattern.pattern;
    error = null;

    try {
      // Extract a clean pattern from displayName by removing common noise
      // "Disney Plus" -> "disney plus"
      const cleanPattern = pattern.displayName
        .toLowerCase()
        .replace(/\s+/g, ' ')  // Normalize spaces
        .trim();

      // createRule expects RuleCreateData object
      await createRule({
        pattern: cleanPattern,
        category: pattern.category,
        person: null,
        normalizedName: pattern.displayName,
        markAsRecurring: true,
        recurringType: selectedType,
        excludeFromAnalytics: false
      });

      // The pattern object has transaction IDs - directly update those transactions
      if (pattern.transactions && pattern.transactions.length > 0) {
        // Get all transactions and update the ones that match
        const allTransactions = await getTransactions();
        const transactionIdsSet = new Set(pattern.transactions);

        // Update each matching transaction
        for (const transaction of allTransactions) {
          if (transactionIdsSet.has(transaction.id)) {
            // Update the transaction with recurring info
            await upsertTransaction({
              ...transaction,
              isRecurring: true,
              recurringType: selectedType,
              displayName: pattern.displayName,
              categorySource: 'rule',
              category: pattern.category
            });
          }
        }

        console.log(`[RecurringDashboard] Marked ${pattern.transactions.length} transactions as recurring`);
      }

      // Remove this pattern from the patterns list (but don't dismiss it)
      // This hides it from the banner since it's now a tracked subscription
      patterns = patterns.filter(p => p.pattern !== pattern.pattern);

      // Show success message
      const typeLabel = selectedType === 'subscription' ? 'subscriptions' : 'bills';
      successMessage = `Rule created for "${pattern.displayName}" - now tracking in ${typeLabel} (${pattern.transactions.length} transactions)`;

      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        successMessage = null;
      }, 3000);

      // Reload recurring transactions to show the newly detected item
      await loadRecurring();
    } catch (err: any) {
      error = err.message;
      console.error('Error creating rule:', err);
    }

    creatingRule = null;
  }

  let visiblePatterns = $derived(
    patterns.filter(p => !isPatternDismissed(p.displayName, dismissed))
  );
</script>

<div class="recurring-dashboard">
  {#if error}
    <Notification type="error" message={error} onDismiss={() => error = null} />
  {/if}

  {#if successMessage}
    <Notification type="success" message={successMessage} onDismiss={() => successMessage = null} />
  {/if}

  {#if loading}
    <div class="loading-message">
      <div class="spinner"></div>
      <p>Loading recurring transactions...</p>
    </div>
  {:else}
    <!-- Summary Cards -->
    <div class="summary-cards">
      <StatCard
        label="Total Monthly"
        value={summary.totalMonthly}
        formatter={(v) => `$${formatAmount(typeof v === 'number' ? v : 0)}`}
        variant="primary"
      />
      <StatCard
        label="Subscriptions"
        value={`$${formatAmount(summary.subscriptions.total)}/mo`}
        subtitle="{summary.subscriptions.count} active"
        variant="success"
      />
      <StatCard
        label="Bills"
        value={`$${formatAmount(summary.bills.total)}/mo`}
        subtitle="{summary.bills.count} active"
        variant="warning"
      />
    </div>

    <!-- New Recurring Patterns Banner -->
    {#if !scanning && visiblePatterns.length > 0}
      <div class="new-patterns-banner">
        <button class="banner-header" onclick={() => patternsExpanded = !patternsExpanded}>
          <div class="banner-title">
            <span class="banner-icon">✨</span>
            <h2>Found {visiblePatterns.length} new recurring pattern{visiblePatterns.length !== 1 ? 's' : ''}</h2>
          </div>
          <span class="expand-icon">{patternsExpanded ? '▼' : '▶'}</span>
        </button>

        {#if patternsExpanded}
          <div class="banner-content">
            <p class="banner-description">
              Review these detected patterns and create rules to automatically track them.
            </p>
            <div class="patterns-list">
              {#each visiblePatterns as pattern (pattern.pattern)}
                <RecurringPatternCard
                  {pattern}
                  isCreating={creatingRule === pattern.pattern}
                  onCreateRule={(selectedType) => createRuleFromPattern(pattern, selectedType)}
                  onMarkCancelled={(selectedType) => markPatternAsCancelled(pattern, selectedType)}
                  onDismiss={() => dismissPattern(pattern)}
                />
              {/each}
            </div>
          </div>
        {/if}
      </div>
    {/if}

    <!-- Possibly Ended Subscriptions -->
    <PossiblyEndedSection
      items={possiblyEndedItems}
      onSnooze={handleSnooze}
      onArchive={handleArchive}
      {snoozingPattern}
      {archivingPattern}
    />

    <!-- Upcoming Payments -->
    <UpcomingPaymentsSection items={upcomingRecurring} />

    <!-- All Recurring Transactions -->
    <div class="all-recurring">
      <h2>All Recurring Transactions</h2>

      <RecurringFilters
        {activeTab}
        totalCount={recurring.length}
        subscriptionsCount={summary.subscriptions.count}
        billsCount={summary.bills.count}
        dismissedCount={dismissed.size}
        onTabChange={(tab) => activeTab = tab}
      />

      {#if activeTab === 'dismissed'}
        <div class="dismissed-patterns-content">
          <p class="tab-description">
            These patterns were marked as "Not Recurring". You can restore them to make them appear in future scans.
          </p>
          <div class="dismissed-list">
            {#each Array.from(dismissed) as pattern (pattern)}
              <div class="dismissed-item">
                <span class="pattern-name">{pattern}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  onclick={() => undismissPattern(pattern)}
                  disabled={undismissingPattern === pattern}
                >
                  {undismissingPattern === pattern ? 'Restoring...' : 'Restore'}
                </Button>
              </div>
            {/each}
          </div>
        </div>
      {:else if filteredRecurring.length === 0}
        <div class="empty-state">
          <p>No recurring transactions detected yet. Check back after you add more transactions</p>
        </div>
      {:else}
        <RecurringItemsTable
          items={filteredRecurring}
          {editingIntervalPattern}
          {editIntervalValue}
          {showConfidenceTooltip}
          onStartEditingInterval={startEditingInterval}
          onCancelEditingInterval={cancelEditingInterval}
          onSaveInterval={saveIntervalEdit}
          onEditIntervalValueChange={(value) => editIntervalValue = value}
          onShowConfidenceTooltip={(pattern, show) => {
            if (show) {
              showConfidenceTooltip[pattern] = true;
            } else {
              showConfidenceTooltip[pattern] = false;
            }
          }}
        />
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">

  .new-patterns-banner {
    margin-bottom: 24px;
    background: var(--color-primary-light);
    border: 2px solid var(--color-primary-border);
    border-radius: var(--radius-lg);
    overflow: hidden;

    .banner-header {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-md) var(--spacing-lg);
      background: none;
      border: none;
      cursor: pointer;
      transition: background var(--transition-fast);

      &:hover {
        background: var(--color-primary-light);
      }

      .banner-title {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);

        .banner-icon {
          font-size: var(--font-size-xl);
        }

        h2 {
          font-size: var(--font-size-base);
          font-weight: var(--font-weight-semibold);
          color: var(--color-primary-dark);
          margin: 0;
        }
      }

      .expand-icon {
        color: var(--color-primary-bright);
        font-size: var(--font-size-sm);
        transition: transform var(--transition-base);
      }
    }

    .banner-content {
      padding: 0 var(--spacing-lg) var(--spacing-lg) var(--spacing-lg);

      .banner-description {
        color: var(--color-primary-dark);
        font-size: var(--font-size-sm);
        margin: 0 0 var(--spacing-md) 0;
      }

      .patterns-list {
        display: flex;
        flex-direction: column;
        gap: 0;
        background: var(--color-background);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }
    }
  }

  .dismissed-patterns-content {
    padding: var(--spacing-xl);
    background: white;
    border-radius: var(--radius-lg);

    .tab-description {
      color: var(--color-text-tertiary);
      font-size: var(--font-size-sm);
      margin: 0 0 var(--spacing-lg) 0;
    }

    .dismissed-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);
    }

    .dismissed-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-md) var(--spacing-md);
      background: var(--color-background-secondary);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      transition: all var(--transition-fast);

      &:hover {
        background: var(--color-background-tertiary);
        border-color: var(--color-border-medium);
      }

      .pattern-name {
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
        font-family: monospace;
        font-weight: var(--font-weight-medium);
      }
    }
  }

  .loading-message {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-2xl);
    text-align: center;
    justify-content: center;

    .spinner {
      width: var(--spinner-size-sm);
      height: var(--spinner-size-sm);
      border: var(--border-width-thick) solid var(--color-border);
      border-top-color: var(--color-primary);
      border-radius: var(--radius-full);
      animation: spin 1s linear infinite;
    }

    p {
      color: var(--color-text-tertiary);
      font-size: var(--font-size-sm);
      margin: 0;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-xl);
  }


  .all-recurring {
    h2 {
      font-size: var(--font-size-2xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-md) 0;
    }
  }

  .empty-state {
    padding: var(--spacing-4xl) var(--spacing-xl);
    text-align: center;
    color: var(--color-text-tertiary);

    p {
      margin: 0;
      font-size: var(--font-size-sm);
    }
  }

</style>
